const SUPABASE_STORAGE_URL = 'supabase.co/storage/v1/object/public/';
const SUPABASE_TRANSFORM_URL = 'supabase.co/storage/v1/render/image/public/';

interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png' | 'avif' | 'origin';
  resize?: 'cover' | 'contain' | 'fill';
}

const UNSUPPORTED_TRANSFORM_EXTENSIONS = new Set(['gif', 'svg']);

// Single baked-thumbnail size reused by gallery and insight cards. Generated
// once at upload/backfill time and served as a static file from the object CDN.
// Fixed 3:2 landscape (600×400) so every card renders a uniform grid with the
// full image and no bars; off-ratio originals are center-cropped via `cover`.
export const THUMBNAIL_WIDTH = 600;
export const THUMBNAIL_HEIGHT = 400;
export const THUMBNAIL_QUALITY = 75;

const SUPABASE_PUBLIC_MARKER = '/storage/v1/object/public/';

interface ParsedStorageUrl {
  origin: string;
  bucket: string;
  path: string;
}

/**
 * Splits a Supabase public storage URL into its origin, bucket and object path.
 * Returns null when the URL is not a Supabase public storage URL.
 */
export const parseSupabasePublicUrl = (url: string | null | undefined): ParsedStorageUrl | null => {
  if (!url) return null;
  const idx = url.indexOf(SUPABASE_PUBLIC_MARKER);
  if (idx === -1) return null;

  const origin = url.slice(0, idx);
  const rest = url.slice(idx + SUPABASE_PUBLIC_MARKER.length).split('?')[0].split('#')[0];
  const slash = rest.indexOf('/');
  if (slash === -1) return null;

  return { origin, bucket: rest.slice(0, slash), path: rest.slice(slash + 1) };
};

/**
 * Derives the deterministic storage path of the baked WebP thumbnail for a
 * given original object path, e.g. `thumbnails/abc.jpg` -> `optimized/thumbnails/abc.webp`.
 */
export const getOptimizedStoragePath = (path: string): string => {
  const withoutExt = path.replace(/\.[^/.]+$/, '');
  return `optimized/${withoutExt}.webp`;
};

/**
 * Returns the public URL of the pre-generated (baked) thumbnail served directly
 * from the storage CDN. Falls back to the original URL for non-Supabase or
 * unsupported (gif/svg) images. The caller should provide an onError fallback
 * to getOptimizedImageUrl for images that have not been baked yet.
 */
export const getThumbnailUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (!canUseSupabaseTransform(url)) return url;
  const parsed = parseSupabasePublicUrl(url);
  if (!parsed) return url;
  const optimizedPath = getOptimizedStoragePath(parsed.path);
  return `${parsed.origin}${SUPABASE_PUBLIC_MARKER}${parsed.bucket}/${optimizedPath}`;
};

const getImageExtension = (url: string): string => {
  try {
    return new URL(url).pathname.split('.').pop()?.toLowerCase() || '';
  } catch {
    return url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() || '';
  }
};

export const canUseSupabaseTransform = (url: string | null | undefined): boolean => {
  if (!url || !url.includes(SUPABASE_STORAGE_URL)) return false;
  return !UNSUPPORTED_TRANSFORM_EXTENSIONS.has(getImageExtension(url));
};

const buildTransformQuery = ({ width, height, quality = 80, format = 'webp', resize }: ImageTransformOptions) => {
  const params = new URLSearchParams();

  if (width) params.set('width', String(width));
  if (height) params.set('height', String(height));
  if (quality) params.set('quality', String(quality));
  if (format) params.set('format', format);
  if (resize) params.set('resize', resize);

  return params.toString();
};

/**
 * Converts a Supabase storage URL to a transformed/optimized URL.
 * Uses Supabase's built-in image transformation API (Pro plan required).
 * Returns the original URL unchanged if it's not a Supabase storage URL.
 */
export const getOptimizedImageUrl = (
  url: string | null | undefined,
  options: ImageTransformOptions = {}
): string => {
  if (!url || !canUseSupabaseTransform(url)) {
    return url || '';
  }

  const { width = 800, quality = 80, format = 'webp', resize = 'contain' } = options;

  const transformedUrl = url.split('?')[0].replace(SUPABASE_STORAGE_URL, SUPABASE_TRANSFORM_URL);
  return `${transformedUrl}?${buildTransformQuery({ ...options, width, quality, format, resize })}`;
};

/**
 * Replaces all raw Supabase storage URLs inside an HTML string with
 * transform URLs. Use this on rich-text article body content so that
 * every embedded image is resized and converted to WebP automatically.
 *
 * Skips URLs that are already going through the transform endpoint to
 * avoid double-encoding.
 */
export const processHtmlImages = (
  html: string,
  options: ImageTransformOptions = {}
): string => {
  if (!html) return html;

  const { width = 1200, quality = 80, format = 'webp', resize = 'contain' } = options;

  // Match any full Supabase raw storage URL (handles http and https)
  return html.replace(
    /https?:\/\/[^"'\s]+supabase\.co\/storage\/v1\/object\/public\/[^"'\s]*/g,
    (match) => {
      // Already a transform URL — skip
      if (match.includes('/render/image/')) return match;
      if (!canUseSupabaseTransform(match)) return match;
      // Already has query params — append after removing any existing ones
      const baseUrl = match.split('?')[0];
      const transformed = baseUrl.replace(
        'supabase.co/storage/v1/object/public/',
        'supabase.co/storage/v1/render/image/public/'
      );
      return `${transformed}?${buildTransformQuery({ ...options, width, quality, format, resize })}`;
    }
  );
};
